import i18n from '@/i18n'
import { formatBytes } from '@/utils'
import { detectImageType } from '@/utils/file/detect-image-type'

export const parseLabelParts = (label: string | undefined): [string | null, string | null] => {
  if (!label) return [null, null]

  const idx = label.indexOf('(')

  if (idx === -1) return [label, null]

  return [label.slice(0, idx).trimEnd(), label.slice(idx).trimEnd()]
}

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name)

/** Rejects files whose bytes are not a real JPEG/PNG/WebP; PDFs on mixed fields pass through. */
export const verifyImageContent = async (file: File): Promise<boolean> =>
  isPdf(file) || (await detectImageType(file)) !== null

export type ValidatedFile = {
  file: File
  errors: string[]
}

type ValidateFileOptions = {
  acceptedTypes: string[]
  maxFileSize: number
  existingNames: ReadonlySet<string>
}

const validateSingleFile = (file: File, options: ValidateFileOptions): string[] => {
  const errors: string[] = []
  const { acceptedTypes, maxFileSize, existingNames } = options

  if (acceptedTypes.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    const isValid = acceptedTypes.some((t) => {
      const lower = t.toLowerCase().replace('.', '')

      return lower === ext || file.type === t
    })

    if (!isValid) {
      errors.push(i18n.t('common:fileUpload.errors.unsupportedType'))
    }
  }

  if (maxFileSize > 0 && file.size > maxFileSize) {
    errors.push(i18n.t('common:fileUpload.errors.tooLarge', { size: formatBytes(maxFileSize) }))
  }

  if (existingNames.has(file.name)) {
    errors.push(i18n.t('common:fileUpload.errors.duplicate', { fileName: file.name }))
  }

  return errors
}

type ValidateBatchOptions = {
  acceptedTypes: string[]
  maxFileSize: number
  remainingSlots: number
  maxFiles: number
  existingNames?: ReadonlySet<string>
}

export type ValidationOutput = {
  valid: File[]
  invalid: ValidatedFile[]
}

export const validateBatch = (files: File[], options: ValidateBatchOptions): ValidationOutput => {
  const valid: File[] = []
  const invalid: ValidatedFile[] = []
  const existingNames = options.existingNames ?? new Set<string>()
  const seenInBatch = new Set<string>()

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    if (i >= options.remainingSlots) {
      invalid.push({
        errors: [i18n.t('common:fileUpload.errors.maxFiles', { count: options.maxFiles })],
        file,
      })
      continue
    }

    const namesForCheck = new Set([...existingNames, ...seenInBatch])
    const errors = validateSingleFile(file, {
      acceptedTypes: options.acceptedTypes,
      existingNames: namesForCheck,
      maxFileSize: options.maxFileSize,
    })

    if (errors.length > 0) {
      invalid.push({ errors, file })
    } else {
      valid.push(file)
      seenInBatch.add(file.name)
    }
  }

  return { invalid, valid }
}

export const getAcceptString = (types: string[]): string =>
  types
    .map((t) => {
      const lower = t.toLowerCase()

      if (lower.startsWith('.') || lower.includes('/')) return lower

      return `.${lower}`
    })
    .join(',')

export const buildAcceptedFilesText = (
  acceptedTypes: string[],
  maxFiles: number,
  maxFileSize: number,
  multiple: boolean,
): string => {
  const parts: string[] = []

  if (acceptedTypes.length > 0) {
    const normalized = acceptedTypes.map((fileType) => fileType.toUpperCase().replace('.', ''))
    const types = normalized
      .filter((fileType) => fileType !== 'JPEG' || !normalized.includes('JPG'))
      .join(', ')

    parts.push(`Accepted Files: ${types}`)
  }

  const maxParts: string[] = []

  if (maxFiles > 0) {
    maxParts.push(`${maxFiles} file${maxFiles !== 1 ? 's' : ''}`)
  }

  if (maxFileSize > 0) {
    const size = formatBytes(maxFileSize).replace(' ', '')

    maxParts.push(`${size}${multiple ? ' each' : ''}`)
  }

  if (maxParts.length > 0) {
    parts.push(`(Max: ${maxParts.join(', ')})`)
  }

  return parts.join(' ')
}

export const buildSlotsText = (
  multiple: boolean,
  remaining: number,
  filesCount: number,
): string | null => {
  if (!multiple) return filesCount === 0 ? 'No files uploaded yet' : null

  return `${remaining} ${remaining === 1 ? 'slot' : 'slots'} remaining`
}
